import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Loader2,
  X,
  User,
  Mail,
  Phone
} from "lucide-react";
import { getProprietaires, createProprietaire, updateProprietaire, deleteProprietaire } from "@/lib/api";

export default function ProprietairesAdminPage() {
  const [proprietaires, setProprietaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProp, setEditingProp] = useState(null);
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    telephone: "",
    iban: "",
    taux_commission: 20,
    notes: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await getProprietaires();
      setProprietaires(res.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProp) {
        await updateProprietaire(editingProp.id, formData);
        toast.success("Propriétaire mis à jour !");
      } else {
        await createProprietaire(formData);
        toast.success("Propriétaire créé !");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (prop) => {
    setEditingProp(prop);
    setFormData({
      nom: prop.nom,
      email: prop.email,
      telephone: prop.telephone,
      iban: prop.iban || "",
      taux_commission: prop.taux_commission,
      notes: prop.notes || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce propriétaire ?")) return;
    try {
      await deleteProprietaire(id);
      toast.success("Propriétaire supprimé !");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setEditingProp(null);
    setFormData({
      nom: "",
      email: "",
      telephone: "",
      iban: "",
      taux_commission: 20,
      notes: ""
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div data-testid="proprietaires-admin-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-display text-[#004333]">Propriétaires partenaires</h2>
          <p className="text-gray-500">{proprietaires.length} propriétaire(s)</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-proprietaire-btn"
        >
          <Plus className="w-4 h-4" />
          Ajouter un propriétaire
        </button>
      </div>

      {proprietaires.length > 0 ? (
        <div className="bg-white border border-gray-200">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left p-4 font-semibold text-[#004333]">Propriétaire</th>
                <th className="text-left p-4 font-semibold text-[#004333]">Contact</th>
                <th className="text-left p-4 font-semibold text-[#004333]">Commission</th>
                <th className="text-left p-4 font-semibold text-[#004333]">Statut</th>
                <th className="text-right p-4 font-semibold text-[#004333]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {proprietaires.map((prop) => (
                <tr key={prop.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#004333] rounded-full flex items-center justify-center text-white font-semibold">
                        {prop.nom.charAt(0)}
                      </div>
                      <span className="font-medium text-[#004333]">{prop.nom}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm text-gray-600">{prop.email}</div>
                    <div className="text-sm text-gray-400">{prop.telephone}</div>
                  </td>
                  <td className="p-4">
                    <span className="font-mono font-bold text-[#C9922A]">{prop.taux_commission}%</span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs font-medium ${
                      prop.statut === "actif" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                    }`}>
                      {prop.statut}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => handleEdit(prop)}
                      className="p-2 text-gray-400 hover:text-[#004333] transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(prop.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 p-16 text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-display text-xl text-[#004333] mb-2">Aucun propriétaire</h3>
          <p className="text-gray-500 mb-6">Ajoutez vos premiers propriétaires partenaires.</p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="btn-primary"
          >
            Ajouter un propriétaire
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-display text-xl text-[#004333]">
                {editingProp ? "Modifier le propriétaire" : "Nouveau propriétaire"}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet *</label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  className="input-hh"
                  data-testid="prop-nom"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-hh"
                  data-testid="prop-email"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                <input
                  type="tel"
                  required
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  className="input-hh"
                  data-testid="prop-telephone"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">IBAN</label>
                <input
                  type="text"
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  className="input-hh"
                  placeholder="FR76 XXXX XXXX..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Taux de commission (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.taux_commission}
                  onChange={(e) => setFormData({ ...formData, taux_commission: parseFloat(e.target.value) })}
                  className="input-hh"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-hh"
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Annuler
                </button>
                <button type="submit" className="btn-primary flex-1" data-testid="prop-submit">
                  {editingProp ? "Mettre à jour" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
